import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, get, remove } from 'firebase/database';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import ConfirmDialog from './ConfirmDialog';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import './admin-page.css';

export default function EditPCList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    get(dbRef(database, 'Refurbished'))
      .then(snap => {
        if (snap.exists()) {
          setItems(Object.entries(snap.val()).map(([key, val]) => ({ key, ...val })));
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const deleteFile = async (filename) => {
    if (!filename) return;
    try { await deleteObject(storageRef(storage, `Refurbished/${filename}`)); } catch {}
  };

  const doDelete = async (item) => {
    setRemoving(item.key);
    setConfirmTarget(null);
    try {
      await deleteFile(item.Cover_image);
      if (item.Images) {
        for (const name of item.Images.split(',').map(s => s.trim()).filter(Boolean)) {
          await deleteFile(name);
        }
      }
      await remove(dbRef(database, `Refurbished/${item.key}`));
      setItems(prev => prev.filter(p => p.key !== item.key));
    } catch (err) {
      setError('Delete failed: ' + err.message);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}><ArrowLeft size={18} /></button>
        <div>
          <h1 className="ap-title">Edit PC</h1>
          <p className="ap-subtitle">Edit or delete a refurbished PC</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      <div className="ap-search-wrap">
        <input className="ap-input" placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 360 }} />
      </div>

      <div className="ap-card">
        {loading && <div className="ap-loading">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="ap-empty"><div className="ap-empty-icon">🖥️</div>No PCs found</div>
        )}
        {!loading && items.length > 0 && (
          <div className="ap-list">
            {items.filter(item => (item.Name || '').toLowerCase().includes(search.toLowerCase())).map(item => (
              <div className="ap-list-item" key={item.key}>
                <span className="ap-list-item-name">{item.Name}</span>
                <div className="ap-list-item-actions">
                  <button
                    className="ap-btn ap-btn-ghost"
                    style={{ padding: '6px 14px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => navigate(`/admin/dashboard/edit-pc/${item.key}`)}
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  <button
                    className="ap-icon-btn ap-icon-btn-danger"
                    title="Delete PC"
                    disabled={removing === item.key}
                    onClick={() => setConfirmTarget(item)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmTarget && (
        <ConfirmDialog
          title="Delete this PC?"
          message={`"${confirmTarget.Name}" and its images will be permanently deleted. This cannot be undone.`}
          confirmLabel="Yes, Delete"
          confirmingLabel="Deleting…"
          confirming={removing === confirmTarget.key}
          onConfirm={() => doDelete(confirmTarget)}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
}
